import React from "react";

/**
 * Speedo gauge for % values (Utilisation / Reliability).
 * - 0-100% half-circle, colour bands: red < lowBand, amber < highBand, green ≥ highBand
 * - lowBand / highBand defaults tuned for plant operations: 60 / 85
 */
export default function ConveyorGauge({
    value = 0,
    label = "UTILISATION",
    lowBand = 60,
    highBand = 85,
    size = 200,
}) {
    const clamped = Math.max(0, Math.min(Number(value) || 0, 100));
    const r = 80;
    const cx = size / 2;
    const cy = size * 0.6;
    const angle = 180 - (clamped / 100) * 180;
    const rad = (angle * Math.PI) / 180;
    const nx = cx + r * Math.cos(rad);
    const ny = cy - r * Math.sin(rad);

    const arc = (fromPct, toPct, stroke) => {
        const a1 = 180 - (fromPct / 100) * 180;
        const a2 = 180 - (toPct / 100) * 180;
        const r1 = (a1 * Math.PI) / 180;
        const r2 = (a2 * Math.PI) / 180;
        const x1 = cx + r * Math.cos(r1);
        const y1 = cy - r * Math.sin(r1);
        const x2 = cx + r * Math.cos(r2);
        const y2 = cy - r * Math.sin(r2);
        return (
            <path
                d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
                stroke={stroke}
                strokeWidth={12}
                fill="none"
                strokeLinecap="butt"
            />
        );
    };

    const color = clamped >= highBand ? "#00A344" : clamped >= lowBand ? "#FFB300" : "#D6202F";

    return (
        <div className="flex flex-col items-center select-none" data-testid={`conveyor-gauge-${label.toLowerCase()}`}>
            <svg viewBox={`0 0 ${size} ${size * 0.7}`} className="w-full">
                {arc(0, lowBand, "#FCD9DC")}
                {arc(lowBand, highBand, "#FFE9B3")}
                {arc(highBand, 100, "#BFE5D0")}
                {/* needle */}
                <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#1F2937" strokeWidth={3} strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={6} fill="#1F2937" />
                {/* ticks */}
                {[0, 25, 50, 75, 100].map((t) => {
                    const a = 180 - (t / 100) * 180;
                    const rA = (a * Math.PI) / 180;
                    const x1 = cx + (r + 4) * Math.cos(rA);
                    const y1 = cy - (r + 4) * Math.sin(rA);
                    const x2 = cx + (r + 11) * Math.cos(rA);
                    const y2 = cy - (r + 11) * Math.sin(rA);
                    const tx = cx + (r + 22) * Math.cos(rA);
                    const ty = cy - (r + 22) * Math.sin(rA);
                    return (
                        <g key={t}>
                            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#64748B" strokeWidth={1.5} />
                            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill="#475569" className="tabular">
                                {t}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <div className="mt-1 text-3xl font-display font-black tabular" style={{ color }}>
                {clamped.toFixed(1)}%
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        </div>
    );
}
